import { Head, Link } from '@inertiajs/react'
import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import vpaBanner from '@/assets/vpa-banner.png'
import { SiteLayout } from '@/components/site-layout'
import { awardCategoryNames } from '@/data/award-categories'
import { apiFetch } from '@/lib/utils'

// ── Top-3 result images ───────────────────────────────────────────────────────
import top3Img1 from '@/assets/top-3/1. Nam VDV Cua Nam.png'
import top3Img2 from '@/assets/top-3/2. Nu VDV cua Nam.png'
import top3Img3 from '@/assets/top-3/3. Đôi VẬN ĐỘNG VIÊN Nam Xuất Sắc Nhất Của Năm.png'
import top3Img4 from '@/assets/top-3/4. Đôi vận động viên Nữ Xuất Sắc Nhất Của Năm.png'
import top3Img5 from '@/assets/top-3/5. Đôi VẬN ĐỘNG VIÊN Nam – Nữ Xuất Sắc Nhất Của Năm.png'
import top3Img6 from '@/assets/top-3/6. vận động viên Nam Trẻ Triển Vọng Của Năm (U18).png'
import top3Img7 from '@/assets/top-3/7. Vận động viên Nữ Trẻ Triển Vọng Của Năm (U18).png'
import top3Img8 from '@/assets/top-3/8. NAM Vận Động Viên  40+ Xuất Sắc Nhất Của Năm.png'
import top3Img9 from '@/assets/top-3/9. Nữ Vận Động Viên 40+ Xuất Sắc Nhất Của Năm.png'
import top3Img10 from '@/assets/top-3/10. Câu Lạc Bộ Hoạt Động Nổi Bật Của Năm.png'
import top3Img11 from '@/assets/top-3/11. Giải Thi Đấu  Của Năm.png'

// ── Types ────────────────────────────────────────────────────────────────────

type Nominee = {
  id: string
  slug: string
  name: string
  votes_count: number
  photo_url: string
}

type Category = {
  id: string
  title: string
  votes_count: number
  nominees: Nominee[]
}

type Section = {
  id: string
  title: string
  position: number
  sponsor: boolean
  categories: Category[]
  nominees?: Nominee[]
}

// ── Constants ─────────────────────────────────────────────────────────────────

const CAMPAIGN_ID = 'e9b2ccf3-fb45-4be8-9d4a-b017fa37586b'
const API_BASE = 'https://baseline.live/api/v2'

// Maps normalized category title (from API) → result image.
// API titles often have trailing ".", "\u2028" (line separator) — we strip those before lookup.
const TOP3_RESULT_IMAGES: Record<string, string> = {
  'Nam Vận Động Viên Pickleball Xuất Sắc Nhất Của Năm': top3Img1,
  'Nữ Vận Động Viên Pickleball Xuất Sắc Nhất Của Năm': top3Img2,
  'Đôi VĐV Nam Xuất Sắc Nhất Của Năm': top3Img3,
  'Đôi VĐV Nữ Xuất Sắc Nhất Của Năm': top3Img4,
  'Đôi VĐV Nam – Nữ Xuất Sắc Nhất Của Năm': top3Img5,
  'VĐV Nam Trẻ Triển Vọng Của Năm (U18)': top3Img6,
  'VĐV Nữ Trẻ Triển Vọng Của Năm (U18)': top3Img7,
  'Nam Vận Động Viên Pickleball 40+ Xuất Sắc Nhất Của Năm': top3Img8,
  'Nữ Vận Động Viên Pickleball 40+ Xuất Sắc Nhất Của Năm': top3Img9,
  'Câu Lạc Bộ Hoạt Động Nổi Bật Của Năm': top3Img10,
  'Giải thi đấu của năm': top3Img11,
}

function getResultImage(title: string): string | undefined {
  const normalized = title.replace(/[\u2028\u2029.]+$/, '').trim()
  return TOP3_RESULT_IMAGES[normalized]
}

// ── Animation variants ────────────────────────────────────────────────────────

const fadeUp = { hidden: { opacity: 0, y: 18 }, visible: { opacity: 1, y: 0 } }
const fadeIn = { hidden: { opacity: 0 }, visible: { opacity: 1 } }

// ── Sub-components ────────────────────────────────────────────────────────────

function SkeletonCard() {
  return (
    <div className="flex flex-col gap-2">
      <div className="aspect-square w-full animate-pulse rounded-2xl bg-[#042133]/8" />
      <div className="h-3 w-3/4 animate-pulse rounded-full bg-[#042133]/8" />
      <div className="h-3 w-1/2 animate-pulse rounded-full bg-[#042133]/8" />
    </div>
  )
}

function NomineeCard({ nominee, rank, objectContain }: { nominee: Nominee; rank: number; objectContain?: boolean }) {
  const badgeBg =
    rank === 1
      ? 'bg-[#FCC632] text-[#042133]'
      : rank === 2
        ? 'bg-[#b0b8c1] text-white'
        : rank === 3
          ? 'bg-[#cd7f32] text-white'
          : 'bg-[#042133]/60 text-white'

  return (
    <Link
      href={`/de-cu/${nominee.slug}`}
      className="group flex flex-col overflow-hidden rounded-2xl border border-[#042133]/10 bg-white/90 shadow-sm transition hover:shadow-md hover:border-[#042133]/20 justify-between"
    >
      <div className="px-3 pt-3 pb-2.5 sm:px-4 sm:pt-4">
        <p className="line-clamp-2 font-semibold text-[#042133] text-xs leading-snug sm:text-sm">
          {nominee.name}
        </p>
        <p className="mt-0.5 text-[10px] text-[#5b7788]">{nominee.votes_count} phiếu</p>
      </div>
      <div className="relative aspect-square w-full overflow-hidden bg-[#042133]/5">
        <span className={`absolute left-2 top-2 z-10 rounded-full px-1.5 py-0.5 text-[9px] font-bold ${badgeBg}`}>
          #{rank}
        </span>
        {nominee.photo_url ? (
          <img
            src={nominee.photo_url}
            alt={nominee.name}
            className={`h-full w-full transition duration-300 group-hover:scale-105 ${objectContain ? 'object-contain p-2' : 'object-cover'}`}
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-[#5b7788] text-4xl">
            🏆
          </div>
        )}
      </div>
    </Link>
  )
}

function CategoryBlock({ category }: { category: Category }) {
  const resultImage = getResultImage(category.title)

  return (
    <div className="rounded-2xl border border-[#042133]/10 bg-white/80 p-4 shadow-[0_10px_40px_-20px_rgba(4,33,51,0.12)] sm:p-5">
      <div className="mb-3">
        <h3 className="font-semibold text-[#042133] text-sm leading-snug sm:text-base">
          {category.title}
        </h3>
      </div>
      {resultImage ? (
        <img
          src={resultImage}
          alt={`Kết quả: ${category.title}`}
          className="w-full rounded-xl object-contain"
          loading="lazy"
        />
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {(() => {
            const sorted = [...category.nominees].sort((a, b) => b.votes_count - a.votes_count)
            return sorted.map((nominee) => {
              const rank = sorted.findIndex(n => n.votes_count === nominee.votes_count) + 1
              return <NomineeCard key={nominee.id} nominee={nominee} rank={rank} />
            })
          })()}
        </div>
      )}
    </div>
  )
}

function SectionBlock({ section, index }: { section: Section; index: number }) {
  const numeral = ['I', 'II', 'III', 'IV', 'V'][index] ?? String(index + 1)
  const nominees = section.nominees ?? []
  const sorted = [...nominees].sort((a, b) => b.votes_count - a.votes_count)

  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className="space-y-4"
    >
      <div className="flex items-center gap-3">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#042133] font-bold text-[10px] text-white">
          {numeral}
        </span>
        <h2 className="font-semibold text-[#042133] text-sm uppercase tracking-[0.15em] sm:text-base">
          {section.title}
        </h2>
      </div>
      {section.sponsor ? (
        <div className="rounded-2xl border border-[#042133]/10 bg-white/80 p-4 shadow-[0_10px_40px_-20px_rgba(4,33,51,0.12)] sm:p-5">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {sorted.map((nominee) => {
              const rank = sorted.findIndex((n) => n.votes_count === nominee.votes_count) + 1
              return <NomineeCard key={nominee.id} nominee={nominee} rank={rank} objectContain />
            })}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {section.categories.map((cat) => (
            <CategoryBlock key={cat.id} category={cat} />
          ))}
        </div>
      )}
    </motion.div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function HomeIndex() {
  const [sections, setSections] = useState<Section[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function loadData() {
      try {
        const campaignRes = await apiFetch(`${API_BASE}/voting_campaigns/${CAMPAIGN_ID}`)
        if (!campaignRes.ok) throw new Error('Không thể tải dữ liệu chiến dịch.')
        const campaign = await campaignRes.json()

        type RawNominee = { id: string; slug: string; name: string; votes_count: number; photo_url: string }
        type RawCat = { id: string; title: string; votes_count: number; icon_url?: string }
        type RawSection = { id: string; title: string; position: number; sponsor?: boolean; voting_nominees?: RawNominee[] }

        const rawSections: RawSection[] =
          campaign.voting_sections ?? campaign.sections ?? campaign.data?.sections ?? []

        const sectionDetails = await Promise.all(
          rawSections.map(async (s) => {
            if (s.sponsor && s.voting_nominees && s.voting_nominees.length > 0) {
              return { ...s, rawCats: [] as RawCat[], rawNominees: s.voting_nominees }
            }
            const res = await apiFetch(`${API_BASE}/voting_sections/${s.id}`)
            if (!res.ok) return { ...s, rawCats: [] as RawCat[], rawNominees: [] as RawNominee[] }
            const data = await res.json()
            const isSponsor = data.sponsor ?? false
            if (isSponsor) {
              const rawNominees: RawNominee[] = data.voting_nominees ?? data.nominees ?? data.data?.nominees ?? []
              return { ...s, sponsor: true, rawCats: [] as RawCat[], rawNominees }
            }
            const rawCats: RawCat[] = data.voting_categories ?? data.categories ?? data.data?.categories ?? []
            return { ...s, sponsor: false, rawCats, rawNominees: [] as RawNominee[] }
          })
        )

        type SectionWithRaw = RawSection & { rawCats: RawCat[]; rawNominees: RawNominee[] }

        const sectionsWithNominees: Section[] = await Promise.all(
          (sectionDetails as SectionWithRaw[]).map(async (s) => {
            if (s.sponsor) {
              return { id: s.id, title: s.title, position: s.position, sponsor: true, categories: [], nominees: s.rawNominees }
            }
            const rawCats = s.rawCats ?? []
            const categories: Category[] = await Promise.all(
              rawCats.map(async (cat) => {
                const res = await apiFetch(`${API_BASE}/voting_categories/${cat.id}`)
                if (!res.ok) return { ...cat, nominees: [] }
                const data = await res.json()
                const rawNominees: RawNominee[] =
                  data.voting_nominees ?? data.nominees ?? data.data?.nominees ?? []
                return { ...cat, nominees: rawNominees }
              })
            )
            return { id: s.id, title: s.title, position: s.position, sponsor: false, categories }
          })
        )

        setSections(sectionsWithNominees.sort((a, b) => a.position - b.position))
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Đã có lỗi xảy ra.')
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [])

  return (
    <SiteLayout>
      <Head title="Kết Quả — Vietnam Pickleball Awards 2025">
        <meta
          name="description"
          content="Kết quả chính thức Vietnam Pickleball Awards 2025 - Tôn vinh những con người, dự án và khoảnh khắc đã góp phần tạo nên hành trình phát triển đầy cảm hứng của Pickleball Việt Nam."
        />
        <meta name="keywords" content="Vietnam Pickleball Awards, Pickleball Việt Nam, giải thưởng pickleball, kết quả, VPA 2025" />

        <meta property="og:type" content="website" />
        <meta property="og:title" content="Kết Quả — Vietnam Pickleball Awards 2025" />
        <meta
          property="og:description"
          content="Tôn vinh những con người, dự án và khoảnh khắc đã góp phần tạo nên hành trình phát triển đầy cảm hứng của Pickleball Việt Nam."
        />
        <meta property="og:image" content="https://pickleballawards.vn/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="1200" />
        <meta property="og:locale" content="vi_VN" />
        <meta property="og:site_name" content="Vietnam Pickleball Awards 2025" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Kết Quả — Vietnam Pickleball Awards 2025" />
        <meta
          name="twitter:description"
          content="Tôn vinh những con người, dự án và khoảnh khắc đã góp phần tạo nên hành trình phát triển đầy cảm hứng của Pickleball Việt Nam."
        />
        <meta name="twitter:image" content="https://pickleballawards.vn/og-image.jpg" />
      </Head>

      {/* Banner */}
      <motion.section
        variants={fadeIn}
        initial="hidden"
        animate="visible"
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="mx-auto w-full max-w-5xl px-4 pt-4 sm:px-6 sm:pt-6"
      >
        <img
          src={vpaBanner}
          alt="Vietnam Pickleball Awards 2025"
          className="h-auto w-full rounded-2xl object-cover shadow-[0_30px_90px_-60px_rgba(4,33,51,0.6)]"
        />
      </motion.section>

      {/* Hero */}
      <motion.section
        variants={fadeUp}
        initial="hidden"
        animate="visible"
        transition={{ duration: 0.5, delay: 0.25, ease: 'easeOut' }}
        className="mx-auto flex max-w-4xl flex-col items-center px-4 pt-6 text-center sm:px-6 sm:pt-8"
      >
        <span className="mb-3 inline-flex items-center gap-1.5 rounded-full border border-[#FCC632]/50 bg-[#FCC632]/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#8a6a00]">
          🏆 Đã kết thúc
        </span>
        <h1 className="font-display font-semibold text-3xl tracking-tight sm:text-4xl md:text-5xl">
          Kết quả chính thức
          <br />
          Vietnam Pickleball Awards 2025
        </h1>
        <p className="mt-3 max-w-xxl text-[#5b7788] text-sm leading-relaxed sm:mt-4">
          Đêm Gala vinh danh trao giải đã diễn ra vào ngày 23/03/2026 tại thành phố Hồ Chí Minh.
          <br />
          Xin chúc mừng tất cả các đề cử đã được vinh danh.
        </p>
        <p className="mt-3 text-[#5b7788] text-[10px] uppercase tracking-[0.2em]">
          Gala · 23/03/2026 · TP. Hồ Chí Minh
        </p>
      </motion.section>

      {/* Leaderboard teaser */}
      <Link
        href="/bang-xep-hang"
        className="mx-auto mt-6 flex max-w-5xl items-center gap-3 rounded-2xl border border-[#FCC632]/40 bg-[#FCC632]/8 px-5 py-4 transition hover:bg-[#FCC632]/15 sm:mt-8 sm:px-6"
      >
        <span className="text-2xl">📊</span>
        <div className="flex-1">
          <p className="font-semibold text-[#042133] text-sm">Bảng xếp hạng</p>
          <p className="text-[#5b7788] text-xs leading-snug">Xem kết quả bình chọn cuối cùng</p>
        </div>
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 shrink-0 text-[#042133]/40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 12h14M12 5l7 7-7 7"/>
        </svg>
      </Link>

      {/* Result sections */}
      <section className="mx-auto mt-10 max-w-5xl space-y-10 px-4 pb-10 sm:mt-12 sm:px-6">
        {loading && (
          <div className="space-y-10">
            {[0, 1, 2].map((si) => (
              <div key={si} className="space-y-4">
                <div className="h-5 w-48 animate-pulse rounded-full bg-[#042133]/8" />
                {[0, 1].map((ci) => (
                  <div
                    key={ci}
                    className="rounded-2xl border border-[#042133]/10 bg-white/80 p-5"
                  >
                    <div className="mb-4 h-4 w-40 animate-pulse rounded-full bg-[#042133]/8" />
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                      {[0, 1, 2, 3].map((n) => (
                        <SkeletonCard key={n} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center text-red-700 text-sm">
            {error}
          </div>
        )}

        {!loading &&
          !error &&
          sections.map((section, index) => (
            <SectionBlock
              key={section.id}
              section={section}
              index={index}
            />
          ))}
      </section>

      {/* Marquee */}
      <motion.section
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className="mx-auto mt-6 max-w-5xl px-4 pb-16 sm:mt-10 sm:px-6 sm:pb-20"
      >
        <div className="rounded-full border border-[#042133]/10 bg-white/80 px-4 py-2.5 text-[#5b7788] text-[9px] uppercase tracking-[0.25em] sm:px-6 sm:py-3 sm:text-[10px]">
          <div className="relative overflow-hidden">
            <div className="flex w-max animate-marquee items-center gap-4 whitespace-nowrap sm:gap-6">
              {awardCategoryNames.map((category) => (
                <span key={`category-${category}`} className="whitespace-nowrap">
                  {category}
                </span>
              ))}
              {awardCategoryNames.map((category) => (
                <span key={`category-duplicate-${category}`} className="whitespace-nowrap">
                  {category}
                </span>
              ))}
            </div>
          </div>
        </div>
      </motion.section>
    </SiteLayout>
  )
}
